import { UserService } from '@shared/services/user.service';
import { UserAccessLevelService } from '@shared/services/userAccessLevelService';
import { ProjectService } from '@shared/services/project.service';
import { VersionService } from '@shared/services/version.service';
import { RequestService } from '@shared/services/request.service';
import { StreamService } from '@shared/services/stream.service';
import { IProject } from '@shared/interfaces/IProject';
import { TranslationService } from '@shared/services/translation.service';
import { BIService } from '@shared/services/bi.service';
import { StorageService } from '@shared/services/storage.service';
import { ITranslations } from '@shared/interfaces/ITranslations';
import { ProjectAccessLevelService } from '@shared/services/projectAccessLevel.service';
// eslint-disable-next-line import/no-cycle
import { AuthService } from './services/auth.service';
import { UserFeaturesNamesType, userFeaturesProhibitedList } from './featuresProhibitedList/userFeaturesProhibitedList';
import { config } from './config/config';
import { BIEventNames } from './interfaces/BI/BIEventNames.enum';
import {
	ProjectPackagesFeaturesNamesType,
	projectPackagesFeaturesProhibitedList,
} from './featuresProhibitedList/projectPackagesFeaturesProhibitedList';
import localTranslationsJson from '@shared/translations/translations.json';
import { createServiceClient } from '@shared/contracts/core/client';
import { activitiesContracts } from '@shared/contracts/activities';
import { projectConfigContracts } from '@shared/contracts/projectconfig';
import { entitiesContracts } from '@shared/contracts/entities';
import { channelMessengerContracts } from '@shared/contracts/channelMessenger';
import { issuesContracts } from '@shared/contracts/issues';
import { locationEngineContracts } from '@shared/contracts/locationengine';
import { notificationEngineContracts } from '@shared/contracts/notificationEngine';
import { safetyEngineContracts } from '@shared/contracts/safetyEngine';
import { siteNetIntegratorContracts } from '@shared/contracts/siteNetIntegrator';
import { reportsEngineContracts } from '@shared/contracts/reportsEngine';
import { storageAccessContracts } from '@shared/contracts/storageAccess';

const userService: UserService = new UserService();
const projectService: ProjectService = new ProjectService();
const userAccessLevelService: UserAccessLevelService<UserFeaturesNamesType> =
	new UserAccessLevelService<UserFeaturesNamesType>(userFeaturesProhibitedList, userService);
const projectAccessLevelService: ProjectAccessLevelService<ProjectPackagesFeaturesNamesType> =
	new ProjectAccessLevelService<ProjectPackagesFeaturesNamesType>(
		projectPackagesFeaturesProhibitedList,
		projectService
	);
const versionService: VersionService = new VersionService();
const requestService: RequestService = new RequestService(
	config.apiGateway,
	userService,
	projectService,
	versionService
);
const authService: AuthService = new AuthService(requestService, userService);
const streamService: StreamService = new StreamService(config, userService, projectService);
const storageService: StorageService = new StorageService(requestService);
const chosenProject: IProject | undefined = projectService.getChosenProject();

const translationService: TranslationService = new TranslationService(
	chosenProject?.language || config.defaultLanguage,
	async () => storageService.getTranslationsCsv(),
	localTranslationsJson as unknown as ITranslations
);

const biService: BIService<BIEventNames> = new BIService<BIEventNames>(config, versionService);

// Typed contract clients, same wiring as `frontend/src/servicesInitializers.ts`.
// Mobile built every URL as a template string, so a contract change was invisible
// to it — a renamed path or a new required param surfaced as a runtime 404 rather
// than a compile error. Going through the client makes both a type error.
const api = <T extends Record<string, unknown>>(path: string, contracts: T) =>
	createServiceClient(path, contracts, { requestService });

const activitiesApi = api('/activities', activitiesContracts);
const projectConfigApi = api('/projectConfig', projectConfigContracts);
const entitiesApi = api('/entities', entitiesContracts);
const channelMessengerApi = api('/channelMessenger', channelMessengerContracts);
const issuesApi = api('/issues', issuesContracts);
const locationEngineApi = api('/location', locationEngineContracts);
const notificationEngineApi = api('/notification', notificationEngineContracts);
const safetyEngineApi = api('/safety', safetyEngineContracts);
const siteNetIntegratorApi = api('/siteNetIntegrator', siteNetIntegratorContracts);
const reportsEngineApi = api('/reports', reportsEngineContracts);
const storageAccessApi = api('/storage', storageAccessContracts);

export {
	userAccessLevelService,
	projectAccessLevelService,
	streamService,
	userService,
	requestService,
	authService,
	versionService,
	storageService,
	translationService,
	projectService,
	biService,
	activitiesApi,
	projectConfigApi,
	entitiesApi,
	channelMessengerApi,
	issuesApi,
	locationEngineApi,
	notificationEngineApi,
	safetyEngineApi,
	siteNetIntegratorApi,
	reportsEngineApi,
	storageAccessApi,
};
